import { useEffect, useState } from 'react';
import first from 'lodash.first';
import { TFilterResult } from 'types/shared';
import { ICountryFlagProps } from 'types/shared';

export default useTerritoryGridData;

/*
 * This hook creates and returns
 * - an array of territory rows with rank, country info and values based on the active metric
 *
 * @param {Array} territoryGridData the data retrieved from the /api/territory/grid endpoint
 * @param {String} activeMetric which metric values to gather.
 * @returns {Object} normalized territory rows
 */
function useTerritoryGridData(
  territoryGridData: any[],
  countries: ICountryFlagProps[],
  filteredCountryId: string
): TerritoryWithCountryDataRow[] | undefined {
  const [territoryRows, setTerritoryRows] = useState<
    TerritoryWithCountryDataRow[] | undefined
  >();

  useEffect(() => {
    if (!territoryGridData || !countries || !countries.length) return;

    const entityData = first<any>(territoryGridData).entityData;
    const entityCountries: any[] = entityData.countries;
    const rows = entityCountries.map(mapToRowData);
    const rowsWithCountryData = rows.map<TerritoryWithCountryDataRow>(
      addCountryMetaData(countries)
    );

    if (filteredCountryId) {
      const filteredCountry = entityCountries.find(
        findHighlightedId(filteredCountryId)
      );
      if (filteredCountry) {
        const filteredRow = [filteredCountry].map(mapToRowData);
        filteredRow[0].highlighted = true;
        const filteredRowWithData =
          filteredRow.map<TerritoryWithCountryDataRow>(
            addCountryMetaData(countries)
          );
        const top4RowsWithoutHighlightedCountry = rowsWithCountryData
          .filter(filterHighlightedCountry(filteredCountryId))
          .slice(0, 4);

        return setTerritoryRows(
          filteredRowWithData.concat(top4RowsWithoutHighlightedCountry)
        );
      }
    }

    setTerritoryRows(rowsWithCountryData);
  }, [territoryGridData, countries, filteredCountryId]);

  return territoryRows;
}

interface ITerritoryRow {
  countryId: string;
  values: any;
  rank: string;
  highlighted?: boolean;
}

type TerritoryWithCountryDataRow = ITerritoryRow & ICountryFlagProps;

function mapToRowData(countryInfo: any): ITerritoryRow {
  return {
    countryId: countryInfo.countryId,
    values: first<any>(countryInfo.data),
    rank: first<any>(countryInfo.data).countryRank,
  };
}

function addCountryMetaData(
  countries: ICountryFlagProps[]
): (row: ITerritoryRow) => TerritoryWithCountryDataRow {
  return function (row: ITerritoryRow): TerritoryWithCountryDataRow {
    const countryData = countries.find(
      (c) => c.id === row.countryId
    ) as ICountryFlagProps;
    return { ...row, ...countryData };
  };
}

function filterHighlightedCountry(
  filteredCountryId: string
): TFilterResult<ITerritoryRow> {
  return function (row: ITerritoryRow): boolean {
    return row.countryId !== filteredCountryId;
  };
}

function findHighlightedId(
  filteredCountryId: string
): TFilterResult<ITerritoryRow> {
  return function (row: ITerritoryRow): boolean {
    return row.countryId === filteredCountryId;
  };
}
